import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

/**
 * MemberLedger — shows the current user's deposit ledger entries.
 * Used by Driver, Hobby Farmer, and (in future) Client dashboards.
 */
export default function MemberLedger() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/ledger/me")
      .then(({ data }) => setEntries(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total = entries
    .filter((e) => e.status === "deposited")
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);

  if (loading) {
    return (
      <div className="bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500">
        Loading ledger…
      </div>
    );
  }

  if (!entries.length) {
    return (
      <div
        data-testid="ledger-empty"
        className="bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">
          Your Ledger
        </div>
        <p className="mt-3 text-sm">
          No deposits yet. Earnings will appear here the moment an admin verifies an order.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="member-ledger" className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">
            Total Deposited
          </div>
          <div className="mt-1 font-serif text-4xl text-emerald-900">
            ${total.toFixed(2)}
          </div>
        </div>
        <div className="text-xs text-stone-600 font-sans">
          {entries.length} ledger entr{entries.length === 1 ? "y" : "ies"}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 uppercase text-stone-600 tracking-[0.15em] text-xs">
            <tr>
              <th className="p-4 text-left">When</th>
              <th className="p-4 text-left">Order</th>
              <th className="p-4 text-left">Amount</th>
              <th className="p-4 text-left">Method</th>
              <th className="p-4 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((l) => (
              <tr
                key={l.id}
                className="border-t border-stone-100"
                data-testid={`my-ledger-${l.id}`}
              >
                <td className="p-4 font-sans text-stone-600">
                  {new Date(l.created_at).toLocaleString()}
                </td>
                <td className="p-4 font-sans text-stone-600">{l.order_id}</td>
                <td className="p-4 font-serif text-stone-900">
                  ${Number(l.amount).toFixed(2)}
                </td>
                <td className="p-4 font-sans text-stone-600">{l.payout_method || "—"}</td>
                <td className="p-4">
                  <Badge
                    className={
                      l.status === "deposited"
                        ? "bg-emerald-800 text-stone-50"
                        : "bg-amber-700 text-stone-50"
                    }
                  >
                    {l.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-stone-500 font-sans px-1">
        Deposits are funds owed to you that admins have verified for an order. The actual transfer
        to your PayPal or CashApp may take 1–2 business days depending on the method.
      </p>
    </div>
  );
}
